import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db, firebaseEnv } from "../firebase";
import PageHeader from "../components/PageHeader";

type SurveyStatus = "published" | "unpublished" | "deleted";

type SurveyRow = {
  id: string;
  title: string;
  status: SurveyStatus;
  userId: string | null;
  ownerId: string | null;
  roles: Record<string, string>;
  memberIds: string[];
};

type UserOption = {
  uid: string;
  email: string;
  label: string;
};

const STATUS_ORDER: SurveyStatus[] = ["published", "unpublished", "deleted"];

function normalizeStatus(data: any): SurveyStatus {
  if (data?.status === "published" || data?.status === "unpublished" || data?.status === "deleted") {
    return data.status;
  }
  return data?.published === true ? "published" : "unpublished";
}

export default function AdminSurveys() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [surveys, setSurveys] = useState<SurveyRow[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [savingSurveyId, setSavingSurveyId] = useState<string | null>(null);

  const userByUid = useMemo(() => {
    const map = new Map<string, UserOption>();
    users.forEach((u) => map.set(u.uid, u));
    return map;
  }, [users]);

  const load = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setErrMsg("You need to sign in to view surveys.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrMsg(null);
    try {
      const meSnap = await getDoc(doc(db, "users", currentUser.uid));
      const myRole = meSnap.exists() ? (meSnap.data() as any)?.accountRole : undefined;
      const admin = myRole === "admin";
      setIsAdmin(admin);
      if (!admin) {
        setErrMsg("Admin access is required.");
        setSurveys([]);
        setUsers([]);
        setLoading(false);
        return;
      }

      const [surveySnap, userSnap] = await Promise.all([
        getDocs(collection(db, "surveys")),
        getDocs(collection(db, "users")),
      ]);

      const nextUsers: UserOption[] = userSnap.docs
        .map((u) => {
          const data = u.data() as any;
          const email = data?.email ?? "";
          const display = (data?.displayName || `${data?.firstName || ""} ${data?.lastName || ""}`.trim()).trim();
          return {
            uid: u.id,
            email,
            label: display ? `${display} (${email || u.id})` : (email || u.id),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      const nextSurveys: SurveyRow[] = surveySnap.docs
        .map((s) => {
          const data = s.data() as any;
          return {
            id: s.id,
            title: data?.title || "(Untitled)",
            status: normalizeStatus(data),
            userId: data?.userId ?? null,
            ownerId: data?.ownerId ?? null,
            roles: (data?.roles ?? {}) as Record<string, string>,
            memberIds: Array.isArray(data?.memberIds) ? data.memberIds : [],
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title));

      setUsers(nextUsers);
      setSurveys(nextSurveys);
    } catch (err: any) {
      setErrMsg(err?.message || "Failed to load admin survey data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(
    () =>
      STATUS_ORDER.map((status) => ({
        status,
        rows: surveys.filter((s) => s.status === status),
      })),
    [surveys]
  );

  const ownerLabel = (uid: string | null | undefined) => {
    if (!uid) return "None";
    return userByUid.get(uid)?.label || uid;
  };

  const changeOwner = async (surveyId: string, newOwnerUid: string) => {
    const survey = surveys.find((s) => s.id === surveyId);
    if (!survey || !newOwnerUid) return;
    const previousOwner = survey.ownerId ?? survey.userId;
    if (previousOwner === newOwnerUid) return;

    const roles = { ...(survey.roles || {}) };
    if (previousOwner && roles[previousOwner] === "owner" && previousOwner !== newOwnerUid) {
      roles[previousOwner] = "editor";
    }
    roles[newOwnerUid] = "owner";

    const memberSet = new Set<string>(survey.memberIds || []);
    if (previousOwner) memberSet.add(previousOwner);
    memberSet.add(newOwnerUid);
    const memberIds = Array.from(memberSet);

    setSavingSurveyId(surveyId);
    setErrMsg(null);
    try {
      await updateDoc(doc(db, "surveys", surveyId), {
        userId: newOwnerUid,
        ownerId: newOwnerUid,
        roles,
        memberIds,
        updatedAt: serverTimestamp(),
      });

      setSurveys((current) =>
        current.map((row) =>
          row.id === surveyId
            ? { ...row, userId: newOwnerUid, ownerId: newOwnerUid, roles, memberIds }
            : row
        )
      );
    } catch (err: any) {
      setErrMsg(err?.message || "Failed to update survey owner.");
    } finally {
      setSavingSurveyId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading surveys…</Typography>
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{errMsg || "Admin access is required."}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Survey Master"
        subtitle={`Admin-only survey owner controls (${firebaseEnv} environment)`}
        actions={<Button variant="outlined" onClick={load}>Refresh</Button>}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Owner transfer sets both <code>ownerId</code> and <code>userId</code> to the selected user and keeps ACL membership aligned.
      </Alert>

      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}

      <Stack spacing={2}>
        {grouped.map(({ status, rows }) => (
          <Paper key={status} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
                {status}
              </Typography>
              <Chip label={`${rows.length} surveys`} size="small" />
            </Stack>

            {rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No surveys in this status.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {rows.map((row) => {
                  const selectedOwner = row.ownerId ?? row.userId ?? "";
                  const disabled = savingSurveyId === row.id;
                  return (
                    <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: 600 }}>{row.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current owner: {ownerLabel(selectedOwner)}
                        </Typography>
                        <FormControl size="small" sx={{ maxWidth: 480 }}>
                          <Select
                            value={selectedOwner}
                            disabled={disabled}
                            displayEmpty
                            onChange={(e) => changeOwner(row.id, e.target.value)}
                          >
                            {users.map((u) => (
                              <MenuItem key={u.uid} value={u.uid}>
                                {u.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
